export type SkillFileTreeNode = {
  /** Segment name (e.g. SKILL.md or references). */
  name: string;
  /** Full relative path for files; folder prefix without trailing slash. */
  path: string;
  kind: "file" | "folder";
  children?: SkillFileTreeNode[];
};

/**
 * Build a nested folder tree from flat relative skill file paths.
 * Folders are derived from path segments; files are leaves. `folders` adds
 * folders that hold no files yet.
 */
export function buildSkillFileTree(
  paths: readonly string[],
  folders: readonly string[] = []
): SkillFileTreeNode[] {
  type MutableNode = {
    name: string;
    path: string;
    kind: "file" | "folder";
    childrenByName: Map<string, MutableNode>;
  };

  const rootChildren = new Map<string, MutableNode>();

  const entries = [
    ...paths.map((path) => ({ path, leafKind: "file" as const })),
    ...folders.map((path) => ({ path, leafKind: "folder" as const })),
  ];

  for (const { path: rawPath, leafKind } of entries) {
    const normalized = rawPath.trim().replace(/^\/+/, "").replace(/\/+$/, "");
    if (!normalized) {
      continue;
    }
    const segments = normalized.split("/").filter((segment) => segment.length > 0);
    if (segments.length === 0) {
      continue;
    }

    let currentMap = rootChildren;
    let prefix = "";

    for (let index = 0; index < segments.length; index += 1) {
      const segment = segments[index];
      if (!segment) {
        continue;
      }
      const isLeaf = index === segments.length - 1;
      prefix = prefix ? `${prefix}/${segment}` : segment;

      let node = currentMap.get(segment);
      if (!node) {
        node = {
          name: segment,
          path: prefix,
          kind: isLeaf ? leafKind : "folder",
          childrenByName: new Map(),
        };
        currentMap.set(segment, node);
      } else if (!isLeaf && node.kind === "file") {
        // Path had a file where a folder is needed — promote to folder.
        node.kind = "folder";
      }

      if (!isLeaf) {
        currentMap = node.childrenByName;
      }
    }
  }

  const toTree = (nodes: Map<string, MutableNode>): SkillFileTreeNode[] => {
    return [...nodes.values()]
      .map((node) => {
        if (node.kind === "folder") {
          return {
            name: node.name,
            path: node.path,
            kind: "folder" as const,
            children: toTree(node.childrenByName),
          };
        }
        return {
          name: node.name,
          path: node.path,
          kind: "file" as const,
        };
      })
      .sort((left, right) => {
        if (left.kind !== right.kind) {
          return left.kind === "folder" ? -1 : 1;
        }
        return left.name.localeCompare(right.name);
      });
  };

  return toTree(rootChildren);
}

export function collectFolderPaths(nodes: readonly SkillFileTreeNode[]): string[] {
  const paths: string[] = [];
  for (const node of nodes) {
    if (node.kind === "folder") {
      paths.push(node.path);
      if (node.children) {
        paths.push(...collectFolderPaths(node.children));
      }
    }
  }
  return paths;
}
